import React, { useEffect, useState } from "react";
import AddInsuranceProviderModal from "./AddInsuranceProvider";
import ClaimTemplatesModal from "./ClaimTemplatesModal";
import {
  InsuranceProvider,
  InsuranceProviderFormData,
} from "../../interfaces/insuranceProviders";
import {
  getInsuranceProviderById,
  getInsuranceProviders,
} from "../../apis/insuranceProviders";
import MastersBreadcrumb from "../../components/MastersBreadcrumb";

const InsuranceProvidersList: React.FC = () => {
  const [providers, setProviders] = useState<InsuranceProvider[]>([]);
  const [openModal, setOpenModal] = useState(false);
  const [editableId, setEditableId] = useState<number | null>(null);
  const [selectedProvider, setSelectedProvider] =
    useState<InsuranceProviderFormData | null>(null);
  const [templateModal, setTemplateModal] = useState<{
    open: boolean;
    providerId: number;
    providerName: string;
  }>({ open: false, providerId: 0, providerName: "" });

  useEffect(() => {
    fetchProviders();
  }, []);

  const unwrapList = (res: any): InsuranceProvider[] => {
    const data = res?.data ?? res;
    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.data)) return data.data;
    if (Array.isArray(res?.data?.data)) return res.data.data;
    return [];
  };

  const fetchProviders = async () => {
    try {
      const res: any = await getInsuranceProviders();
      setProviders(unwrapList(res));
    } catch (error) {
      console.log("Error fetching insurance providers:", error);
    }
  };

  const openForCreate = () => {
    setEditableId(null);
    setSelectedProvider(null);
    setOpenModal(true);
  };

  const openForEdit = async (id: number) => {
    try {
      const res: any = await getInsuranceProviderById(id);
      const data = res?.data ?? res;
      setEditableId(id);
      setSelectedProvider({
        name: data?.name ?? "",
        policyNumberPrefix: data?.policyNumberPrefix ?? data?.policy_number_prefix ?? "",
        contactPerson: data?.contactPerson ?? data?.contact_person ?? "",
        phone: data?.phone ?? "",
        email: data?.email ?? "",
        address: data?.address ?? "",
      });
      setOpenModal(true);
    } catch (error) {
      console.log("Error fetching insurance provider:", error);
    }
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      <MastersBreadcrumb current="Insurance Providers" />
      <div className="sm:flex sm:items-center sm:justify-between">
        <div>
          <h1 className="text-base font-semibold leading-6 text-gray-900">
            Insurance Providers
          </h1>
          <p className="mt-2 text-sm text-gray-700">
            Manage insurance companies and their claim form label mappings.
          </p>
        </div>

        <button
          type="button"
          onClick={openForCreate}
          className="px-3 py-2 text-sm font-semibold text-white rounded-md shadow-sm bg-primary hover:bg-hover"
        >
          Add Insurance Provider
        </button>
      </div>

      <AddInsuranceProviderModal
        open={openModal}
        onClose={() => {
          setOpenModal(false);
          setEditableId(null);
          setSelectedProvider(null);
        }}
        onSuccess={fetchProviders}
        editableId={editableId}
        providerData={selectedProvider}
      />

      <ClaimTemplatesModal
        open={templateModal.open}
        onClose={() => setTemplateModal({ open: false, providerId: 0, providerName: "" })}
        providerId={templateModal.providerId}
        providerName={templateModal.providerName}
      />

      <div className="flow-root mt-8">
        <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
          <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
            <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg">
              <table className="min-w-full divide-y divide-gray-300">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="py-3.5 px-3 text-left text-sm font-semibold text-gray-900">
                      Name
                    </th>
                    <th className="py-3.5 px-3 text-left text-sm font-semibold text-gray-900">
                      Policy Prefix
                    </th>
                    <th className="py-3.5 px-3 text-left text-sm font-semibold text-gray-900">
                      Contact Person
                    </th>
                    <th className="py-3.5 px-3 text-left text-sm font-semibold text-gray-900">
                      Phone
                    </th>
                    <th className="py-3.5 px-3 text-left text-sm font-semibold text-gray-900">
                      Email
                    </th>
                    <th className="py-3.5 px-3 text-left text-sm font-semibold text-gray-900">
                      Address
                    </th>
                    <th className="py-3.5 px-3 text-left text-sm font-semibold text-gray-900">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {providers.length === 0 ? (
                    <tr>
                      <td
                        colSpan={7}
                        className="py-4 text-center text-sm text-gray-500"
                      >
                        No insurance providers found
                      </td>
                    </tr>
                  ) : (
                    providers.map((p) => (
                      <tr key={p.id}>
                        <td className="py-4 px-3 text-sm font-medium text-gray-900">
                          {p.name}
                        </td>
                        <td className="py-4 px-3 text-sm text-gray-700">
                          {p.policyNumberPrefix ?? "-"}
                        </td>
                        <td className="py-4 px-3 text-sm text-gray-700">
                          {p.contactPerson ?? "-"}
                        </td>
                        <td className="py-4 px-3 text-sm text-gray-700">
                          {p.phone ?? "-"}
                        </td>
                        <td className="py-4 px-3 text-sm text-gray-700">
                          {p.email ?? "-"}
                        </td>
                        <td className="py-4 px-3 text-sm text-gray-700 max-w-[20rem] truncate">
                          {p.address ?? "-"}
                        </td>
                        <td className="py-4 px-3 text-sm text-gray-700">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => openForEdit(p.id)}
                              className="px-3 py-1 text-sm text-white bg-primary rounded"
                            >
                              Edit
                            </button>
                            <button
                              onClick={() =>
                                setTemplateModal({
                                  open: true,
                                  providerId: p.id,
                                  providerName: p.name,
                                })
                              }
                              className="px-3 py-1 text-sm text-white bg-indigo-600 rounded"
                            >
                              Claim Templates
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InsuranceProvidersList;
